import { commandsStore } from "@/store/commandsStore";
import { gptStore } from "@/store/gptStore";
import {
	isCarouselStart,
	isDisplaySlotConfig,
	isPageDataCommand,
	isTriggerAuctionCommand,
} from "@/ts/guards";
import type { AdUnitPathModule } from "@/ts/module";
import type {
	PageDataCommand,
	StartCommand,
	TriggerAuctionCommand,
} from "@/ts/types";

export class AdUnitPath implements AdUnitPathModule {
	name = "AdUnitPath" as const;

	async init(): Promise<void> {
		subscribeToPageData();
		subscribeToTriggerAuction();
		subscribeToStart();
		await Promise.resolve();
	}
}

const getLongestAdUnitPath = (storedPath: string, newPath: string) => {
	if (!storedPath) {
		return newPath;
	}
	if (!newPath) {
		return storedPath;
	}
	// prevent overwriting "sitename" with "video.sitename"
	if (newPath.length > storedPath.length && newPath.startsWith(storedPath)) {
		return newPath;
	}
	return storedPath;
};

const subscribeToPageData = (): void => {
	const { selectPageDataCommand } = commandsStore.getState().selectors;

	commandsStore.subscribe(
		selectPageDataCommand,
		(command: PageDataCommand | undefined) => {
			if (isPageDataCommand(command)) {
				updateAdUnitPathIfNeeded(command.data.adUnitPath);
			}
		},
	);
};

const subscribeToTriggerAuction = (): void => {
	const { selectTriggerAuctionCommand } = commandsStore.getState().selectors;

	commandsStore.subscribe(
		selectTriggerAuctionCommand,
		(command: TriggerAuctionCommand | undefined) => {
			// validate because this selector can return undefined
			if (isTriggerAuctionCommand(command) && command.slots?.length > 0) {
				const firstSlot = command.slots[0];
				if (isDisplaySlotConfig(firstSlot)) {
					updateAdUnitPathIfNeeded(firstSlot.adUnitPath);
				}
			}
		},
	);
};

const subscribeToStart = (): void => {
	const { selectStartCommand } = commandsStore.getState().selectors;

	commandsStore.subscribe(
		selectStartCommand,
		(command: StartCommand | undefined) => {
			if (isCarouselStart(command)) {
				const adUnitPath = command.adUnitPath;
				updateAdUnitPathIfNeeded(adUnitPath);
			}
		},
	);
};

const updateAdUnitPathIfNeeded = (newPath: string | undefined): void => {
	if (newPath) {
		const { pageAdUnitPath, actions } = gptStore.getState();
		const updatedPath = getLongestAdUnitPath(pageAdUnitPath, newPath);
		if (updatedPath !== pageAdUnitPath) {
			actions.addAdUnitPath(updatedPath);
		}
	}
};
